import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import ClientBase, { useRevertableQuery } from "@citrine/client";
import { STATIC_QUERY_OPTIONS } from "@citrine/client/utils";
import type { IParsedCSVContent } from "@citrine/utils/csv";
import { parseCsvToObject } from "@citrine/utils/csv";
import type {
  IFileUploadStatus,
  IFileUploadWithContent,
} from "@citrine/utils/s3";
import { upload } from "@citrine/utils/s3";

import type {
  FileLinkIDs,
  ICitrineUploadRequestResponse,
  IFile,
  IFileContentLink,
  IFileLink,
  IUploadRequestData,
} from "./file";
import { extractMetaFromVersionedUrl } from "./file";
import { uploadRequestResponseToS3Config } from "./utils";

interface UploadOptions {
  files: FileWithMetadata[];
}

interface FileWithMetadata {
  file: File;
  description?: string;
  title?: string;
}

export interface IFileUploadComplete extends IFileUploadStatus {
  fileLink?: IFileLink;
  file?: IFile;
}

export class FileClient extends ClientBase<IFile> {
  protected constructor(endpoint: string) {
    super({
      endpoint,
      findField: "files",
    });
  }

  static getInstanceForProject(projectId: string, datasetId: string) {
    return new FileClient(
      projectId && datasetId
        ? `/api/v1/projects/${projectId}/datasets/${datasetId}/files`
        : "",
    );
  }

  static getInstanceForTeam(teamId: string, datasetId?: string) {
    return datasetId
      ? new FileClient(`/api/v1/teams/${teamId}/datasets/${datasetId}/files`)
      : new FileClient(`/api/v1/teams/${teamId}/files`);
  }

  static getInstanceForFileLinkIDs(ids: FileLinkIDs | undefined) {
    return ids && "teamId" in ids
      ? this.getInstanceForTeam(ids.teamId, ids.datasetId)
      : this.getInstanceForProject(ids?.projectId ?? "", ids?.datasetId ?? "");
  }

  /**
   * Hook to fetch file information of a single file.
   * @param  {string|undefined} fileIdAndVersion
   * @param  {UseQueryOptions<IFile>} options?
   */
  public useGet<Data = IFile>(
    fileIdAndVersion: string,
    options?: Omit<
      UseQueryOptions<IFile, AxiosError, Data>,
      "queryKey" | "queryFn"
    >,
  ) {
    return super.useGet(fileIdAndVersion, {
      ...options,
      enabled: Boolean(this.endpoint && options?.enabled !== false),
    });
  }

  /** Retrieve a pre-signed URL for your file
   */
  async getContentLink(
    fileId: string,
    fileVersion: string,
  ): Promise<IFileContentLink> {
    const response = await this.request<IFileContentLink>({
      method: "GET",
      url: `${this.endpoint}/${fileId}/versions/${fileVersion}/content-link`,
    });
    return response;
  }

  useGetContentLink(
    fileId: string,
    fileVersion: string,
    options?: Omit<UseQueryOptions<IFileContentLink>, "queryKey" | "queryFn">,
  ): UseQueryResult<IFileContentLink> {
    return useRevertableQuery({
      ...STATIC_QUERY_OPTIONS,
      ...options,
      queryKey: [
        ...this.getModelCacheKey(`${fileId}/versions/${fileVersion}`),
        "content",
      ],
      queryFn: () => this.getContentLink(fileId, fileVersion),
      enabled: Boolean(
        this.endpoint && fileId && fileVersion && options?.enabled !== false,
      ),
    });
  }

  // retrives the actual file content from s3, this function differs from thee others beecausee they return the file info but not the contents
  // the actual file is stored in s3 and the only way to get that file is to use the presigned URL given to us from the backend
  async getFileContent(preSignedReadLink: string): Promise<string> {
    const response = await this.request<string>({
      method: "GET",
      url: preSignedReadLink,
    });
    return response;
  }

  /**
   * This function downloads the file from s3 (handling the few API calls necessary
   * to get the s3 url) given an IFile object.
   *
   * Note that this hook returns a query so you have to do: x.data to access the csv content that is returned
   */
  useGetCSVFileContent(
    versionedUrl: string | undefined,
  ): UseQueryResult<IParsedCSVContent> {
    // we have to get the real file id and version from the version URL
    const metadata = versionedUrl
      ? extractMetaFromVersionedUrl(versionedUrl)
      : undefined;
    const { fileId, fileVersion } = metadata ?? {};

    return useQuery({
      queryKey: [
        ...this.getQueryCachePrefix(),
        fileId,
        fileVersion,
        "csv-file-content",
      ],
      queryFn: async () => {
        // once we have id and verison then we need to get the URL where we can get the actual file content
        const contentLink = await this.getContentLink(fileId!, fileVersion!);

        // the pre_signed_read_link is the link we use to download the file from s3 (it contains everything you need to download the file)
        if (contentLink) {
          const csvString = await this.getFileContent(
            contentLink.pre_signed_read_link,
          );

          // just returning the actual data for now, when we want to handle the
          // errors in the UI then send the whole csvAsObject object
          const csvAsObject = await parseCsvToObject(csvString);

          return csvAsObject.data;
        }

        return [];
      },

      enabled: Boolean(this.endpoint && fileId && fileVersion),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
    });
  }

  async completeUpload(
    statusReport: IFileUploadStatus,
  ): Promise<IFileUploadComplete> {
    if (statusReport.status === "DONE") {
      const { file_info } = await this.request<{
        file_info: {
          file_id: string;
          version: string;
        };
      }>({
        method: "PUT",
        url: `${this.endpoint}/uploads/${statusReport.uploadId}/complete`,
        data: { s3_version: statusReport.value?.VersionId },
      });
      this.invalidateAfterMutation();

      const file = await this.get(
        `${file_info.file_id}/versions/${file_info.version}`,
      );

      return {
        ...statusReport,
        file,
        fileLink: {
          type: "file_link",
          url: `${this.endpoint}/${file_info.file_id}/versions/${file_info.version}`,
          filename: statusReport.fileName,
        },
      };
    }

    return statusReport;
  }

  async upload(files: FileWithMetadata[]): Promise<IFileUploadComplete[]> {
    const fileMetadata: IUploadRequestData[] = files.map(
      ({ file, title, description }) => ({
        file_name: file.name,
        metadata: title ? { title } : {},
        mime_type: file.type,
        size: file.size,
        description,
      }),
    );

    const uploadRequestResponse =
      // need to call this initial function to get the details of where
      // to actually upload the file
      await this.request<ICitrineUploadRequestResponse>({
        method: "POST",
        url: `${this.endpoint}/uploads`,
        data: { files: fileMetadata },
      });

    // loop over files and construct data structure necessary for upload
    const fileUploadsWithContent = files
      .map(({ file }) => ({
        content: file,
        fileName: file.name,
        uploadId: uploadRequestResponse.uploads.find(
          (u) => u.file_name === file.name,
        )?.upload_id,
      }))
      .filter(({ uploadId }) => uploadId) as IFileUploadWithContent[];

    const uploadedFileResponses = await Promise.allSettled(
      fileUploadsWithContent
        .map((fileUploadWithContent) =>
          uploadRequestResponseToS3Config(
            uploadRequestResponse,
            fileUploadWithContent,
          ),
        )
        .filter(Boolean)
        .map((config) => upload(config)),
    );

    return Promise.all(
      uploadedFileResponses.map((response) => {
        if (response.status === "fulfilled") {
          return this.completeUpload(response.value);
        }
        return response.reason;
      }),
    );
  }

  useUpload(
    options?: Omit<
      UseMutationOptions<IFileUploadComplete[], unknown, UploadOptions>,
      "mutationFn"
    >,
  ): UseMutationResult<IFileUploadComplete[], unknown, UploadOptions> {
    return useMutation({
      ...options,
      mutationFn: ({ files }) => this.upload(files),
    });
  }
}
